import FuzzySet from "fuzzyset";

export interface ExpectedFileMatch<T> {
  id: T;
  baseName: string;
  keywords: string[];
  allowedExtensions: string[];
}

const DEFAULT_THRESHOLD = 0.4;

export const fuzzyMatchFile = <T>(
  uploadedFileName: string,
  expected: ExpectedFileMatch<T>[],
  threshold: number = DEFAULT_THRESHOLD
): { id: T; score: number } | null => {
  const lastDotIndex = uploadedFileName.lastIndexOf(".");
  if (lastDotIndex === -1) return null;

  const uploadedExtension = uploadedFileName.slice(lastDotIndex).toLowerCase();
  const uploadedBaseName = uploadedFileName.slice(0, lastDotIndex).toLowerCase();

  const candidates = expected.filter((f) => f.allowedExtensions.some((ext) => ext.toLowerCase() === uploadedExtension));
  if (candidates.length === 0) return null;

  let bestMatch: ExpectedFileMatch<T> | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    const fuzzySet = FuzzySet([candidate.baseName.toLowerCase()]);
    const fuzzyMatches = fuzzySet.get(uploadedBaseName);
    const fuzzyScore = fuzzyMatches?.[0]?.[0] ?? 0;

    let keywordScore = 0;
    for (const keyword of candidate.keywords) {
      const lowered = keyword.toLowerCase();
      if (uploadedBaseName.includes(lowered)) {
        const isWordBoundary = uploadedBaseName === lowered || new RegExp(`(^|[_\\-\\s])${lowered}([_\\-\\s]|$)`, "i").test(uploadedBaseName);
        keywordScore = Math.max(keywordScore, isWordBoundary ? 0.9 : 0.7);
      }
    }

    const score = Math.max(fuzzyScore, keywordScore);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidate;
    }
  }

  if (bestScore < threshold || !bestMatch) return null;
  return { id: bestMatch.id, score: bestScore };
};
